import { VoidCallback } from '@deqo/foundation';
import React, { useMemo } from 'react';
import { CommandDefinition, bindCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon, NoIcon } from '../../../concepts/resources/Icon';
import { Point } from '../../../platform/geom/Point';
import { Menu } from '../../../ui';
import { useResolve } from '../../Container';
import { exportData } from '../../packages/dataExport/ExportCommands';
import { Camera } from '../Camera';
import { useEditorSessionObserver } from '../EditorSession';
import { cameraFrameContent, cameraLinkToPosition, cameraZoomReset } from '../commands/CameraCommands';
import { paste } from '../commands/ClipboardCommands';
import { lockSetAll, lockSetLayout } from '../commands/ComponentCommands';
import { select, selectAll } from '../commands/SelectionCommands';
import { Physical } from '../entities/Physical';
import { SpatialIndex } from '../utilities/SpatialIndex';
import { IsElevated } from '../workflow/IsElevated';
import { ContextMenuProps } from './ContextMenuProps';

const lockLayout = bindCommand(lockSetLayout, [true]);
const lockEverything = bindCommand(lockSetAll, [true]);
const unlockEverything = bindCommand(lockSetAll, [false]);

const useElementsAtPosition = (position: Point) => {
    const camera = useResolve(Camera);
    const spatial = useResolve(SpatialIndex);
    return useMemo(() => {
        if (!position) return [];
        const mpt = camera.toModel(position);
        const hits = Array.from(spatial.lookup(mpt)).slice(0, 20);
        return hits;
    }, [position]);
};

const createSelectSubMenu = (elmts: Physical[], onClose: VoidCallback) =>
    React.memo(() => {
        const dispatch = useResolve(CommandDispatcher);
        const onSelect = (p: Physical) => {
            onClose();
            dispatch(select, [[p.id]]);
        };
        return <Menu>
            {elmts.filter(x => x.alive && x.mounted() && !x.bounded()).map(x =>
                <Menu.Item key={x.id} onClick={() => onSelect(x)}>
                    <Menu.ItemTextCropped>{x.alive && x.toHumanString()}</Menu.ItemTextCropped>
                    <Menu.Shortcut keys={x.id} />
                </Menu.Item>
            )}
        </Menu>;
    });

export const EmptySelectionContextMenu = React.memo(({ position, onClose }: ContextMenuProps) => {

    const dispatch = useResolve(CommandDispatcher);
    const canLock = useEditorSessionObserver(IsElevated);
    const elmts = useElementsAtPosition(position);

    const onCommand = (command: CommandDefinition<any>) => {
        return () => {
            dispatch(command);
            onClose();
        };
    };

    return <Menu>
        <Menu.Section first>
            <Menu.Item onClick={onCommand(paste)}>
                <NoIcon size="1.2" />
                Paste
                <Menu.Shortcut keys="CTRL+V"></Menu.Shortcut>
            </Menu.Item>
            <Menu.Item onClick={onCommand(selectAll)}>
                <NoIcon size="1.2" />
                Select All
                <Menu.Shortcut keys="CTRL+A"></Menu.Shortcut>
            </Menu.Item>
            {!!elmts.length &&
                <Menu.SubMenuItem menu={createSelectSubMenu(elmts, onClose)}>
                    <NoIcon size="1.2" />
                    Select Specific
                    <Menu.SubMenuIndicator />
                </Menu.SubMenuItem>
            }
        </Menu.Section>
        {canLock && (
            <Menu.Section>
                <Menu.Item onClick={onCommand(lockLayout)}>
                    <NoIcon size="1.2" />
                    Lock Layout
                </Menu.Item>
                <Menu.Item onClick={onCommand(lockEverything)}>
                    <Icon size="1.2" src="deqo://icons/d2/locked" />
                    Lock Everything
                </Menu.Item>
                <Menu.Item onClick={onCommand(unlockEverything)}>
                    <Icon size="1.2" src="deqo://icons/d2/unlocked" />
                    Unlock Everything
                </Menu.Item>
            </Menu.Section>
        )}
        <Menu.Section>
            <Menu.Item onClick={onCommand(cameraZoomReset)}>
                <NoIcon size="1.2" />
                Zoom To 100%
                <Menu.Shortcut keys="CTRL+0"></Menu.Shortcut>
            </Menu.Item>
            <Menu.Item onClick={onCommand(cameraFrameContent)}>
                <NoIcon size="1.2" />
                Zoom To Content
                <Menu.Shortcut keys="CTRL+1"></Menu.Shortcut>
            </Menu.Item>
            <Menu.Item onClick={onCommand(cameraLinkToPosition)}>
                <NoIcon size="1.2" />
                Copy Link To View
            </Menu.Item>
        </Menu.Section>
        <Menu.Section>
            <Menu.Item onClick={onCommand(exportData)}>
                <Icon size="1.2" src="deqo://icons/hero/upload" />
                Export...
                <Menu.Shortcut keys="CTRL+S"></Menu.Shortcut>
            </Menu.Item>
        </Menu.Section>
    </Menu>;
});
